import type {
  AssessmentPhaseStatus,
  AssessmentProgress,
  SpeechPhaseProgress,
  TextPhaseProgress
} from '@/features/nectar/api/models'
import { Text } from '@/ui-kit'
import clsx from 'clsx'

type AssessmentStepperProps = {
  progress: AssessmentProgress
}

type PhaseInfo = {
  index: 1 | 2
  fullLabel: string
  shortLabel: string
  status: AssessmentPhaseStatus
  answered: number
  total: number
}

const buildPhaseInfo = (text: TextPhaseProgress, speech: SpeechPhaseProgress): [PhaseInfo, PhaseInfo] => [
  {
    index: 1,
    fullLabel: 'Language Test',
    shortLabel: 'Language',
    status: text.status,
    answered: text.answeredCount,
    total: text.maxQuestions
  },
  {
    index: 2,
    fullLabel: 'Speech Assessment',
    shortLabel: 'Speech',
    status: speech.status,
    answered: speech.answeredCount,
    total: speech.totalQuestions
  }
]

const renderDots = (count: number, answered: number) =>
  Array.from({ length: count }, (_, i) => {
    const isDone = i < answered
    const isCurrent = i === answered
    return (
      <div
        key={i}
        className={clsx(
          'rounded-full transition-all',
          isCurrent ? 'size-2.5 bg-[#3b82f6] shadow-[0_0_0_3px_rgba(59,130,246,0.25)]' : 'size-2',
          isDone && 'bg-green',
          !isDone && !isCurrent && 'bg-gray-100'
        )}
      />
    )
  })

const ActivePhaseBlock = ({ phase }: { phase: PhaseInfo }) => {
  const answered = Math.min(phase.answered, phase.total)
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <Text type="body3-regular" className="text-gray-90 truncate tracking-wide uppercase">
          Phase {phase.index} of 2 · {phase.fullLabel}
        </Text>
        <Text type="body3-regular" className="text-gray-80 shrink-0 font-mono">
          {answered}/{phase.total}
        </Text>
      </div>
      <div className="flex items-center gap-1">{renderDots(phase.total, answered)}</div>
    </div>
  )
}

const NextPhaseChip = ({ phase }: { phase: PhaseInfo }) => (
  <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-solid border-gray-100 px-3 py-1.5">
    <Text type="body3-regular" className="text-gray-60 tracking-wide whitespace-nowrap uppercase">
      Next ▸ {phase.shortLabel}
    </Text>
    <span className="bg-gray-60/40 h-3 w-px" />
    <div className="flex items-center gap-1">
      {Array.from({ length: phase.total }, (_, i) => (
        <div key={i} className="size-1.5 rounded-full bg-gray-100" />
      ))}
    </div>
  </div>
)

const DonePhaseChip = ({ phase }: { phase: PhaseInfo }) => (
  <div className="border-green/30 bg-green/5 flex shrink-0 items-center rounded-full border border-solid px-3 py-1.5">
    <Text type="body3-regular" className="text-green tracking-wide whitespace-nowrap uppercase">
      ✓ {phase.shortLabel} · Done
    </Text>
  </div>
)

const Divider = () => <div className="h-10 w-px shrink-0 bg-gray-100" />

export const AssessmentStepper = ({ progress }: AssessmentStepperProps) => {
  const [text, speech] = progress.phases
  const [textInfo, speechInfo] = buildPhaseInfo(text, speech)
  const textActive = progress.currentPhase === 'text'

  const active = textActive ? textInfo : speechInfo
  const other = textActive ? speechInfo : textInfo

  return (
    <div className="max-mobile:max-w-[320px] mx-auto flex w-full max-w-[600px] items-center gap-4">
      {!textActive && (
        <>
          <DonePhaseChip phase={other} />
          <Divider />
        </>
      )}
      <ActivePhaseBlock phase={active} />
      {textActive && (
        <>
          <Divider />
          <NextPhaseChip phase={other} />
        </>
      )}
    </div>
  )
}
